import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { GenericAction } from '@/utility/reducers';

export enum SubmitError {
  REQUIRED_AMOUNT = 'REQUIRED_AMOUNT',
  AMOUNT_BIGGER_THAN_BALANCE = 'AMOUNT_BIGGER_THAN_BALANCE',
}

export interface ConvertModalState {
  readonly beneficiaryId: string;
  readonly simulationId: string | null;
  readonly fromAmount: number | null;
  readonly fromCurrency: CurrencyCodeType;
  readonly toCurrency: CurrencyCodeType;
  readonly paymentRail: PaymentRailType;
  readonly validationError: SubmitError | null;
  readonly fundingMethods: readonly any[];
  readonly selectedFundingMethod: any;
}

export enum ConvertModalActionType {
  beneficiaryIdChanged = 'beneficiaryIdChanged',
  fromAmountChanged = 'fromAmountChanged',
  fromCurrencyChanged = 'fromCurrencyChanged',
  toCurrencyChanged = 'toCurrencyChanged',
  paymentRailChanged = 'paymentRailChanged',
  simulationIdChanged = 'simulationIdChanged',
  validationErrorChanged = 'validationErrorChanged',
  fundingMethodsChanged = 'fundingMethodsChanged',
  selectedFundingMethodChanged = 'selectedFundingMethodChanged',
  stateUpdated = 'stateUpdated',
}

export type BeneficiaryIdChangedAction = GenericAction<
  ConvertModalActionType.beneficiaryIdChanged,
  string
>;

export type FromAmountChangedAction = GenericAction<
  ConvertModalActionType.fromAmountChanged,
  number | null
>;

export type FromCurrencyChangedAction = GenericAction<
  ConvertModalActionType.fromCurrencyChanged,
  CurrencyCodeType
>;

export type ToCurrencyChangedAction = GenericAction<
  ConvertModalActionType.toCurrencyChanged,
  CurrencyCodeType
>;

export type PaymentRailChangedAction = GenericAction<
  ConvertModalActionType.paymentRailChanged,
  PaymentRailType
>;

export type SimulationIdChangedAction = GenericAction<
  ConvertModalActionType.simulationIdChanged,
  string | null
>;

export type ValidationErrorChangedAction = GenericAction<
  ConvertModalActionType.validationErrorChanged,
  SubmitError | null
>;

export type FundingMethodsChangedAction = GenericAction<
  ConvertModalActionType.fundingMethodsChanged,
  readonly any[]
>;

export type SelectedFundingMethodChangedAction = GenericAction<
  ConvertModalActionType.selectedFundingMethodChanged,
  any
>;

export type StateUpdatedAction = GenericAction<
  ConvertModalActionType.stateUpdated,
  Partial<ConvertModalState>
>;

export type ConvertModalAction =
  | BeneficiaryIdChangedAction
  | FromAmountChangedAction
  | FromCurrencyChangedAction
  | ToCurrencyChangedAction
  | PaymentRailChangedAction
  | SimulationIdChangedAction
  | ValidationErrorChangedAction
  | FundingMethodsChangedAction
  | SelectedFundingMethodChangedAction
  | StateUpdatedAction;

export const initialState: ConvertModalState = {
  beneficiaryId: '',
  validationError: null,
  fromAmount: null,
  simulationId: null,
  paymentRail: PaymentRailType.unset,
  fromCurrency: CurrencyCodeType.UNSET,
  toCurrency: CurrencyCodeType.UNSET,
  fundingMethods: [],
  selectedFundingMethod: null,
};

export const convertModalReducer = (
  state: ConvertModalState,
  action: ConvertModalAction,
): ConvertModalState => {
  switch (action.type) {
    case ConvertModalActionType.beneficiaryIdChanged:
      if (state.beneficiaryId === action.payload) {
        return state;
      }
      return {
        ...state,
        beneficiaryId: action.payload,
        fromAmount: null,
        validationError: null,
      };
    case ConvertModalActionType.fromAmountChanged:
      return {
        ...state,
        fromAmount: action.payload,
        validationError:
          state.validationError === SubmitError.REQUIRED_AMOUNT ||
          state.validationError === SubmitError.AMOUNT_BIGGER_THAN_BALANCE
            ? null
            : state.validationError,
      };
    case ConvertModalActionType.fromCurrencyChanged:
      return { ...state, fromCurrency: action.payload, fromAmount: null, validationError: null };
    case ConvertModalActionType.toCurrencyChanged:
      return { ...state, toCurrency: action.payload, fromAmount: null, validationError: null };
    case ConvertModalActionType.paymentRailChanged:
      return { ...state, paymentRail: action.payload };
    case ConvertModalActionType.simulationIdChanged:
      return { ...state, simulationId: action.payload };
    case ConvertModalActionType.validationErrorChanged:
      return { ...state, validationError: action.payload };
    case ConvertModalActionType.fundingMethodsChanged:
      return { ...state, fundingMethods: action.payload };
    case ConvertModalActionType.selectedFundingMethodChanged:
      return { ...state, selectedFundingMethod: action.payload };
    case ConvertModalActionType.stateUpdated:
      return { ...state, ...action.payload, fromAmount: null, validationError: null };
    default:
      return state;
  }
};

export const beneficiaryIdChanged = (payload: string): BeneficiaryIdChangedAction => ({
  type: ConvertModalActionType.beneficiaryIdChanged,
  payload: payload,
});

export const fromAmountChanged = (payload: number | null): FromAmountChangedAction => ({
  type: ConvertModalActionType.fromAmountChanged,
  payload: payload,
});

export const fromCurrencyChanged = (payload: CurrencyCodeType): FromCurrencyChangedAction => ({
  type: ConvertModalActionType.fromCurrencyChanged,
  payload: payload,
});

export const toCurrencyChanged = (payload: CurrencyCodeType): ToCurrencyChangedAction => ({
  type: ConvertModalActionType.toCurrencyChanged,
  payload: payload,
});

export const paymentRailChanged = (payload: PaymentRailType): PaymentRailChangedAction => ({
  type: ConvertModalActionType.paymentRailChanged,
  payload: payload,
});

export const simulationIdChanged = (payload: string | null): SimulationIdChangedAction => ({
  type: ConvertModalActionType.simulationIdChanged,
  payload: payload,
});

export const validationErrorChanged = (
  payload: SubmitError | null,
): ValidationErrorChangedAction => ({
  type: ConvertModalActionType.validationErrorChanged,
  payload: payload,
});

export const fundingMethodsChanged = (payload: readonly any[]): FundingMethodsChangedAction => ({
  type: ConvertModalActionType.fundingMethodsChanged,
  payload: payload,
});

export const selectedFundingMethodChanged = (payload: any): SelectedFundingMethodChangedAction => ({
  type: ConvertModalActionType.selectedFundingMethodChanged,
  payload: payload,
});

export const stateUpdated = (payload: Partial<ConvertModalState>): StateUpdatedAction => ({
  type: ConvertModalActionType.stateUpdated,
  payload: payload,
});
